import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Brain,
  ChevronRight,
  FileJson,
  FileText,
  Globe,
  Info,
  Lightbulb,
  Loader2,
  Pencil,
} from 'lucide-react';

import { Button } from 'components/ui/button';
import { Textarea } from 'components/ui/textarea';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { getRegionIcon } from 'constant/model-metadata';
import { LLM_MODEL_REGIONS } from 'constant/llm-model';
import type {
  IGetAgentModelResponse,
  IGetCustomLlmModelsResponse,
} from 'features/chat/agent-chat-module/services/gpt-chat.service';
import {
  getProviderDisplayName,
  getProviderIcon,
  getProviderIconClassName,
} from 'pages/models/models.utils';
import { useTheme } from 'components/core/theme-provider';
import { useIsMobile } from 'hooks/use-mobile';
import { useToast } from 'hooks/use-toast';
import { cn } from 'lib/utils';
import { BasePromptAiModal } from './base-prompt-ai-modal';
import { JsonSchemaEditorModal } from './json-schema-editor-modal';

type ModelSelectionValue = {
  modelName: string;
  provider: string;
  isBlocksProvider: boolean;
  modelId?: string | null;
  temperature: number;
  maxTokens: number;
};

type LlmConfigurationSectionProps = {
  isLoadingLlmModels: boolean;
  availableChatModels: IGetAgentModelResponse;
  isLoadingCustomLlmModels: boolean;
  availableCustomChatModels: IGetCustomLlmModelsResponse['models'];
  currentLlmModel: string;
  currentLlmProvider: string;
  currentLlmModelId: string;
  isBlocksProvider: boolean;
  onModelChange: (value: ModelSelectionValue) => void;
  responseType: string;
  onResponseTypeChange: (value: string) => void;
  responseFormat: string | null;
  onResponseFormatChange: (value: string | null) => void;
  basePrompt: string;
  onBasePromptChange: (value: string) => void;
  autoBasePrompt: string;
  temperatureValue: number;
  onTemperatureChange: (value: number) => void;
  maxResponseValue: number;
  onMaxResponseChange: (value: number) => void;
  isModelParametersOpen: boolean;
  onModelParametersOpenChange: (open: boolean) => void;
  agentName?: string;
  selectedToolIds?: string[];
  disabled?: boolean;
};

const REGION_LOGOS: Record<string, string | undefined> = {
  eu: getRegionIcon(LLM_MODEL_REGIONS.EU),
  ch: getRegionIcon(LLM_MODEL_REGIONS.CH),
  global: undefined,
};

type BlocksModelItem = {
  modelName: string;
  modelLabel: string;
  providerId: string;
  providerLabel: string;
  defaultTemp?: number | null;
  maxTokens?: number | null;
  region?: string;
};

type CustomModelItem = {
  modelName: string;
  displayName: string;
  modelId: string;
  defaultTemp?: number | null;
  maxTokens?: number | null;
};

const clampTemperature = (value?: number | null) => {
  const nextValue = value ?? 0.7;
  return Math.min(Math.max(nextValue, 0), 1);
};

const clampMaxTokens = (value?: number | null) => {
  const nextValue = value ?? 10000;
  return Math.min(Math.max(nextValue, 0), 10000);
};

const normalizeRegion = (region?: string | null) => region?.toLowerCase();

const RegionLogo = ({ region }: { region?: string | null }) => {
  const normalizedRegion = normalizeRegion(region);
  if (!normalizedRegion) return null;

  if (normalizedRegion === 'global') {
    return <Globe className="h-4 w-4 text-muted-foreground" />;
  }

  const icon = REGION_LOGOS[normalizedRegion];
  if (!icon) return null;

  return <img src={icon} alt={normalizedRegion.toUpperCase()} className="h-4 w-4 object-contain" />;
};

export function LlmConfigurationSection({
  isLoadingLlmModels,
  availableChatModels,
  isLoadingCustomLlmModels,
  availableCustomChatModels,
  currentLlmModel,
  currentLlmProvider,
  currentLlmModelId,
  isBlocksProvider,
  onModelChange,
  responseType,
  onResponseTypeChange,
  responseFormat,
  onResponseFormatChange,
  basePrompt,
  onBasePromptChange,
  autoBasePrompt,
  temperatureValue,
  onTemperatureChange,
  maxResponseValue,
  onMaxResponseChange,
  isModelParametersOpen,
  onModelParametersOpenChange,
  agentName,
  selectedToolIds,
  disabled = false,
}: LlmConfigurationSectionProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isJsonEditorOpen, setIsJsonEditorOpen] = useState(false);
  const [isPromptEmptyWarning, setIsPromptEmptyWarning] = useState(false);
  const [isBasePromptAiModalOpen, setIsBasePromptAiModalOpen] = useState(false);
  const [isModelSelectorOpen, setIsModelSelectorOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedProviderGroup, setSelectedProviderGroup] = useState<string | null>(null);
  const modelParametersAnchorRef = useRef<HTMLDivElement | null>(null);
  const [modelParametersPopoverWidth, setModelParametersPopoverWidth] = useState<number>();
  const isMobile = useIsMobile();
  const { resolvedTheme } = useTheme();
  const temperature = temperatureValue.toFixed(2);
  const maxTokens = maxResponseValue;
  const exampleSchema = useMemo(
    () =>
      JSON.stringify(
        {
          answer: 'string',
          question: 'string',
        },
        null,
        2
      ),
    []
  );
  const visibleResponseFormat = responseFormat || exampleSchema;
  const isExampleSchema = visibleResponseFormat === exampleSchema;
  const basePromptTokenCount = useMemo(() => {
    if (!basePrompt.trim()) return 0;

    return basePrompt
      .trim()
      .split(/\s+/)
      .filter(Boolean).length;
  }, [basePrompt]);

  useEffect(() => {
    if (basePrompt.trim()) setIsPromptEmptyWarning(false);
  }, [basePrompt]);

  const handleBasePromptBlur = () => {
    if (basePrompt.trim()) return;

    setIsPromptEmptyWarning(true);
    onBasePromptChange(autoBasePrompt);
    toast({
      variant: 'warning',
      title: t('BASE_PROMPT_CANNOT_BE_EMPTY') || "Base prompt can't be empty",
      description:
        t('DEFAULT_PROMPT_APPLIED_INSTEAD') || 'A default prompt has been applied instead.',
    });
  };

  const blocksMap = useMemo(() => {
    const map: Record<string, BlocksModelItem[]> = {};

    availableChatModels.forEach((item) => {
      const providerKey = item.provider_label;
      if (!map[providerKey]) {
        map[providerKey] = [];
      }

      map[providerKey].push({
        modelName: item.model_name,
        modelLabel: item.model_name_label,
        providerId: item.provider,
        providerLabel: item.provider_label,
        defaultTemp: item.default_temp,
        maxTokens: item.max_tokens,
        region: item.region,
      });
    });

    return map;
  }, [availableChatModels]);

  const customMap = useMemo(() => {
    const map: Record<string, CustomModelItem[]> = {};

    availableCustomChatModels.forEach((item) => {
      if (!map[item.Provider]) {
        map[item.Provider] = [];
      }

      map[item.Provider].push({
        modelName: item.ModelName ?? '',
        displayName: item.DisplayName || item.ModelName,
        modelId: item._id,
        defaultTemp: item.Capabilities?.default_temp,
        maxTokens: item.Capabilities?.max_tokens,
      });
    });

    return map;
  }, [availableCustomChatModels]);

  const blockProviders = useMemo(() => Object.keys(blocksMap), [blocksMap]);
  const customProviders = useMemo(() => Object.keys(customMap), [customMap]);
  const categories = useMemo(() => {
    const nextCategories: string[] = ['custom'];

    if (blockProviders.length > 0) {
      nextCategories.push('blocks');
    }

    return nextCategories;
  }, [blockProviders.length]);

  useEffect(() => {
    if (!isModelParametersOpen) return undefined;

    const updatePopoverWidth = () => {
      if (modelParametersAnchorRef.current) {
        setModelParametersPopoverWidth(modelParametersAnchorRef.current.offsetWidth);
      }
    };

    updatePopoverWidth();
    window.addEventListener('resize', updatePopoverWidth);
    return () => window.removeEventListener('resize', updatePopoverWidth);
  }, [isModelParametersOpen, isMobile]);

  const handleModelSelect = ({
    provider,
    modelName,
    isBlocksModel,
    modelId,
    defaultTemp,
    maxTokens,
  }: {
    provider: string;
    modelName: string;
    isBlocksModel: boolean;
    modelId?: string;
    defaultTemp?: number | null;
    maxTokens?: number | null;
  }) => {
    onModelChange({
      modelName,
      provider,
      isBlocksProvider: isBlocksModel,
      modelId: modelId || null,
      temperature: clampTemperature(defaultTemp),
      maxTokens: clampMaxTokens(maxTokens),
    });

    setIsModelSelectorOpen(false);
    setSelectedCategory(null);
    setSelectedProviderGroup(null);
  };

  let finalDisplayLabel = currentLlmModel || (t('SELECT_A_MODEL') || 'Select a model...');
  let selectedRegion: string | undefined;

  if (currentLlmProvider && currentLlmModel) {
    if (isBlocksProvider) {
      outer: for (const providerLabel of blockProviders) {
        for (const model of blocksMap[providerLabel] || []) {
          if (
            model.providerId === currentLlmProvider &&
            model.modelName === currentLlmModel
          ) {
            finalDisplayLabel = `Blocks / ${model.providerLabel} / ${model.modelLabel}`;
            selectedRegion = model.region;
            break outer;
          }
        }
      }
    } else {
      const providerLabel = getProviderDisplayName(currentLlmProvider);
      const foundModel = (customMap[currentLlmProvider] || []).find(
        (model) =>
          model.modelName === currentLlmModel ||
          (currentLlmModelId ? model.modelId === currentLlmModelId : false)
      );

      if (foundModel) {
        finalDisplayLabel = `My Model / ${providerLabel} / ${foundModel.displayName}`;
      } else {
        finalDisplayLabel = `My Model / ${providerLabel} / ${currentLlmModel}`;
      }
    }
  }

  const currentProviderIcon = getProviderIcon(currentLlmProvider);
  const isLoadingModels = isLoadingLlmModels || isLoadingCustomLlmModels;
  const hasAnyModel = categories.length > 0;

  const renderModelParameterControl = (
    label: string,
    value: number,
    max: number,
    min: number,
    step: number,
    onChange: (nextValue: number) => void
  ) => (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <Info className="h-4 w-4 shrink-0 text-muted-foreground" />
      </div>
      <div
        className={cn(
          'flex gap-3',
          isMobile ? 'flex-col items-stretch' : 'flex-row items-center'
        )}
      >
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(Number(event.target.value))}
          className="h-2 w-full min-w-0 flex-1 cursor-pointer appearance-none rounded-full bg-muted accent-primary disabled:cursor-not-allowed disabled:opacity-50"
        />
        <div
          className={cn(
            'shrink-0 rounded-lg border border-border bg-background px-3 py-2 text-right',
            isMobile ? 'w-full' : 'min-w-[130px]'
          )}
        >
          <span
            className={cn(
              'font-semibold text-foreground',
              isMobile ? 'text-xl' : 'text-2xl'
            )}
          >
            {step < 1 ? value.toFixed(2) : value}
          </span>
          <span className="ml-1 text-sm text-muted-foreground">
            / {step < 1 ? max.toFixed(2) : max}
          </span>
        </div>
      </div>
    </div>
  );

  const renderCategories = () => (
    <div
      role="menu"
      className={cn(
        'p-2',
        isMobile ? 'w-full border-b border-border' : 'min-w-[180px] border-r border-border'
      )}
    >
      {categories.map((category) => {
        const label =
          category === 'custom'
            ? t('MY_MODELS') || 'My Models'
            : t('BLOCKS_MODELS') || 'Blocks Models';
        const isActive = selectedCategory === category;
        const isDisabled = category === 'custom' && customProviders.length === 0;

        return (
          <button
            key={category}
            type="button"
            onClick={() => {
              if (isDisabled) return;
              setSelectedCategory(category);
              setSelectedProviderGroup(null);
            }}
            className={`flex w-full items-center justify-between rounded-md p-2 text-left text-sm transition-colors ${isDisabled
              ? 'cursor-not-allowed opacity-50'
              : isActive
                ? 'bg-primary/10 text-primary dark:bg-accent dark:text-foreground'
                : 'text-foreground hover:bg-muted hover:text-foreground'
              }`}
            disabled={isDisabled}
          >
            <span>{label}</span>
            <ChevronRight className="h-4 w-4" />
          </button>
        );
      })}
    </div>
  );

  const renderProviderPanel = () => {
    if (!selectedCategory) return null;

    const providers = selectedCategory === 'custom' ? customProviders : blockProviders;

    return (
      <div
        role="menu"
        className={cn(
          'p-2',
          isMobile ? 'w-full border-b border-border' : 'min-w-[200px] border-r border-border'
        )}
      >
        {providers.map((provider) => {
          const isActive = selectedProviderGroup === provider;
          const label =
            selectedCategory === 'custom' ? getProviderDisplayName(provider) : provider;

          return (
            <button
              key={provider}
              type="button"
              onClick={() => setSelectedProviderGroup(provider)}
              className={`flex w-full items-center justify-between rounded-md p-2 text-left text-sm transition-colors ${isActive
                ? 'bg-primary/10 text-primary dark:bg-accent dark:text-foreground'
                : 'text-foreground hover:bg-muted hover:text-foreground'
                }`}
            >
              <span>{label}</span>
              <ChevronRight className="h-4 w-4" />
            </button>
          );
        })}
      </div>
    );
  };

  const renderModelsPanel = () => {
    if (!selectedCategory || !selectedProviderGroup) return null;

    if (selectedCategory === 'custom') {
      return (
        <div role="menu" className={cn('p-2', isMobile ? 'w-full' : 'min-w-[220px]')}>
          {(customMap[selectedProviderGroup] || []).map((model) => (
            <button
              key={model.modelId}
              type="button"
              onClick={() =>
                handleModelSelect({
                  provider: selectedProviderGroup,
                  modelName: model.modelName,
                  isBlocksModel: false,
                  modelId: model.modelId,
                  defaultTemp: model.defaultTemp,
                  maxTokens: model.maxTokens,
                })
              }
              className="flex w-full items-center justify-between rounded-md p-2 text-left text-sm text-foreground transition-colors hover:bg-muted"
            >
              <span className="break-words">{model.displayName}</span>
            </button>
          ))}
        </div>
      );
    }

    return (
      <div role="menu" className={cn('p-2', isMobile ? 'w-full' : 'min-w-[240px]')}>
        {(blocksMap[selectedProviderGroup] || []).map((model) => (
          <button
            key={`${model.providerId}-${model.modelName}`}
            type="button"
            onClick={() =>
              handleModelSelect({
                provider: model.providerId,
                modelName: model.modelName,
                isBlocksModel: true,
                defaultTemp: model.defaultTemp,
                maxTokens: model.maxTokens,
              })
            }
            className="flex w-full items-center justify-between rounded-md p-2 text-left text-sm text-foreground transition-colors hover:bg-muted"
          >
            <div className="flex w-full items-center gap-3">
              <span className="break-words">{model.modelLabel}</span>
              <RegionLogo region={model.region} />
            </div>
          </button>
        ))}
      </div>
    );
  };

  return (
    <fieldset
      disabled={disabled}
      className="m-0 min-w-0 space-y-6 border-0 p-0 disabled:opacity-70"
    >
      <div className={cn('space-y-2', isMobile ? 'w-full max-w-full' : 'max-w-sm')}>
        <p className="text-sm font-medium text-foreground">{t('SELECT_MODEL') || 'Select Model'}</p>
        {isLoadingModels ? (
          <div className="flex h-11 items-center gap-2 rounded-md border border-border bg-background px-3 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('LOADING_MODELS') || 'Loading models...'}
          </div>
        ) : hasAnyModel ? (
          <Popover
            open={disabled ? false : isModelSelectorOpen}
            onOpenChange={(open) => {
              if (disabled) return;
              setIsModelSelectorOpen(open);
              if (!open) {
                setSelectedCategory(null);
                setSelectedProviderGroup(null);
              }
            }}
          >
            <PopoverTrigger asChild>
              <button
                type="button"
                disabled={disabled}
                className="flex min-h-[44px] w-full items-center justify-between rounded-lg border border-border bg-background px-4 py-2 text-left transition-colors hover:bg-muted/30 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <div className="flex min-w-0 items-center gap-2 overflow-hidden">
                  {currentProviderIcon ? (
                    <img
                      src={currentProviderIcon}
                      alt={currentLlmProvider || t('PROVIDER') || 'Provider'}
                      className={getProviderIconClassName(currentLlmProvider, resolvedTheme)}
                    />
                  ) : null}
                  <div className="flex min-w-0 items-center gap-2 overflow-hidden">
                    <span className="truncate text-sm font-medium text-foreground">
                      {finalDisplayLabel}
                    </span>
                    <RegionLogo region={selectedRegion} />
                  </div>
                </div>
                <ChevronRight
                  className={cn(
                    'ml-2 h-4 w-4 shrink-0 text-muted-foreground transition-transform',
                    isModelSelectorOpen && 'rotate-90'
                  )}
                />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              side="bottom"
              sideOffset={8}
              collisionPadding={16}
              className="w-auto max-w-[min(100vw-2rem,720px)] max-h-[min(420px,70vh)] overflow-auto rounded-md border border-border bg-background p-0 shadow-lg"
            >
              <div className={cn(isMobile ? 'flex w-full flex-col' : 'flex min-w-0')}>
                {renderCategories()}
                {renderProviderPanel()}
                {renderModelsPanel()}
              </div>
            </PopoverContent>
          </Popover>
        ) : (
          <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
            {t('NO_CHAT_MODELS_AVAILABLE') || 'No chat models available.'}
          </div>
        )}
      </div>

      {/* Model Parameters */}

      {/* <div
        ref={modelParametersAnchorRef}
        className={cn('w-full space-y-3', isMobile ? 'max-w-full' : 'max-w-sm')}
      >
        <p className="text-sm font-medium text-foreground">{t('MODEL_PARAMETERS') || 'Model Parameters'}</p>
        <Popover
          open={isModelParametersOpen}
          onOpenChange={onModelParametersOpenChange}
        >
          <PopoverAnchor asChild>
            <div className="flex w-full flex-wrap gap-3">
              <button
                type="button"
                onClick={() => onModelParametersOpenChange(true)}
                className="rounded-lg bg-muted px-4 py-3 text-sm text-foreground transition-colors hover:bg-muted/80"
              >
                <span className="text-muted-foreground">{t('TEMPERATURE_LABEL') || 'Temperature:'}</span>{' '}
                <span className="font-semibold text-primary dark:text-[#ffffff]">{temperature}</span>
              </button>
              <button
                type="button"
                onClick={() => onModelParametersOpenChange(true)}
                className="rounded-lg bg-muted px-4 py-3 text-sm text-foreground transition-colors hover:bg-muted/80"
              >
                <span className="text-muted-foreground">
                  {t('MAXIMUM_RESPONSE_LABEL') || 'Maximum response:'}
                </span>{' '}
                <span className="font-semibold text-primary dark:text-[#ffffff]">{maxTokens}</span>
              </button>
            </div>
          </PopoverAnchor>
          <PopoverContent
            align="start"
            side="bottom"
            sideOffset={12}
            collisionPadding={16}
            style={
              modelParametersPopoverWidth
                ? { width: modelParametersPopoverWidth }
                : undefined
            }
            className="w-auto max-w-[520px] rounded-lg border border-border bg-background p-0"
          >
            <div className={cn('space-y-6', isMobile ? 'p-4' : 'space-y-8 p-6')}>
              <h3
                className={cn(
                  'font-semibold text-foreground',
                  isMobile ? 'text-lg' : 'text-2xl'
                )}
              >
                {t('MODEL_PARAMETERS') || 'Model Parameters'}
              </h3>
              {renderModelParameterControl(
                t('TEMPERATURE') || 'Temperature',
                temperatureValue,
                1,
                0,
                0.01,
                onTemperatureChange
              )}
              {renderModelParameterControl(
                t('MAXIMUM_RESPONSE') || 'Maximum Response',
                maxResponseValue,
                10000,
                2,
                1,
                onMaxResponseChange
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div> */}

      {/* Response Type */}

      {/* <div className="max-w-sm space-y-2">
        <p className="text-sm font-medium text-foreground">{t('RESPONSE_TYPE') || 'Response Type'}</p>
        <Select value={responseType} onValueChange={onResponseTypeChange}>
          <SelectTrigger className="h-11">
            <SelectValue placeholder={t('SELECT_RESPONSE_TYPE') || 'Select response type'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="text">
              <span className="inline-flex items-center gap-2">
                <FileText className="h-4 w-4" />
                {t('TEXT') || 'Text'}
              </span>
            </SelectItem>
            <SelectItem value="json">
              <span className="inline-flex items-center gap-2">
                <FileJson className="h-4 w-4" />
                {t('JSON') || 'JSON'}
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
      </div> */}

      {responseType === 'json' ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-foreground">{t('RESPONSE_SCHEMA') || 'Response Schema'}</p>
              <Info className="h-4 w-4 text-muted-foreground" />
            </div>
            <Button
              type="button"
              variant="outline"
              className="h-11 gap-2 px-4 text-sm"
              onClick={() => setIsJsonEditorOpen(true)}
            >
              <Pencil className="h-4 w-4" />
              {t('EDIT_JSON') || 'Edit JSON'}
            </Button>
          </div>

          <div className="space-y-2">
            {isExampleSchema ? (
              <div className="rounded-lg border-l-4 border-primary bg-blue-50 px-4 py-3 text-primary">
                <div className="flex items-start gap-2">
                  <Lightbulb className="mt-0.5 h-4 w-4 shrink-0" />
                  <p className="text-sm">
                    {t('EXAMPLE_SCHEMA_CLICK_EDIT_JSON_TO_CUSTOMIZE') ||
                      'Example schema • Click Edit JSON to customize'}
                  </p>
                </div>
              </div>
            ) : null}

            <div className="overflow-hidden rounded-lg border border-border bg-background">
              <div className="max-h-[220px] overflow-auto p-5">
                <pre className="whitespace-pre-wrap break-words text-sm leading-7 text-foreground">
                  {visibleResponseFormat}
                </pre>
              </div>
              <div className="flex justify-center border-t border-border bg-muted/20 px-4 py-2">
                <div className="h-1 w-12 rounded-full bg-border" />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-foreground">{t('BASE_PROMPT') || 'Base Prompt'}</p>
            <Info className="h-4 w-4 text-muted-foreground" />
          </div>

          {/* AI Base Prompt */}
          {/* <Button
            type="button"
            variant="secondary"
            className="h-9 rounded-full border border-primary/30 bg-muted px-4 text-sm text-primary dark:text-[#ffffff] hover:bg-muted/80"
            onClick={() => setIsBasePromptAiModalOpen(true)}
          >
            <Brain className="h-4 w-4" />
            <span className="dark:text-[#ffffff]">AI</span>
          </Button> */}
        </div>

        <div
          className={cn(
            'overflow-hidden rounded-lg border border-border bg-background',
            isPromptEmptyWarning && 'border-destructive'
          )}
        >
          <Textarea
            value={basePrompt}
            disabled={disabled}
            onChange={(event) => onBasePromptChange(event.target.value)}
            onBlur={handleBasePromptBlur}
            placeholder={
              disabled
                ? t('ASK_AGENT_OWNER_TO_ADD_BASE_PROMPT') ||
                'Ask the agent owner to add a base prompt'
                : t('ADD_BASE_PROMPT_FOR_THIS_AGENT') || 'Add a base prompt for this agent'
            }
            className="min-h-[260px] resize-y border-0 px-5 py-4 text-sm leading-6 shadow-none focus-visible:ring-0"
          />
          <div className="flex items-center justify-end border-t border-border bg-muted/20 px-4 py-2 text-sm text-muted-foreground">
            {basePromptTokenCount}{' '}
            {basePromptTokenCount === 1 ? t('TOKEN') || 'Token' : t('TOKENS') || 'Tokens'}
          </div>
        </div>
      </div>

      <JsonSchemaEditorModal
        open={disabled ? false : isJsonEditorOpen}
        initialValue={visibleResponseFormat}
        onOpenChange={setIsJsonEditorOpen}
        onSave={onResponseFormatChange}
      />
      <BasePromptAiModal
        open={disabled ? false : isBasePromptAiModalOpen}
        onOpenChange={setIsBasePromptAiModalOpen}
        onUse={onBasePromptChange}
        agentName={agentName}
        basePromptValue={basePrompt}
        toolIds={selectedToolIds}
      />
    </fieldset>
  );
}
